export type WriteApprovalEntityKind =
  | "custom-role"
  | "contract-type"
  | "draft-template"
  | "draft-legal-role"
  | "draft-clause-library";

function str(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function clampText(text: string, maxLen: number): string {
  const t = text.trim();
  if (t.length <= maxLen) return t;
  return `${t.slice(0, maxLen - 1).trim()}…`;
}

export function formatWriteApprovalSummary(
  kind: WriteApprovalEntityKind,
  proposal: Record<string, unknown>
): { headline: string; body: string } {
  const name = str(proposal.name);
  const value = str(proposal.value);
  const roleKey = str(proposal.role_key);
  const guidelines = str(proposal.guidelines);
  const instructions = str(proposal.instructions);
  const blurb = str(proposal.blurb);
  const sectionKey = str(proposal.section_key);
  const clauseCategory = str(proposal.clause_category);
  const posture = str(proposal.posture);
  const language = str(proposal.language);

  switch (kind) {
    case "custom-role":
    case "draft-legal-role": {
      const headline = roleKey || name || "New role";
      const parts = [name && roleKey ? `${name} · ${roleKey}` : name || roleKey].filter(Boolean);
      const intro = parts.join("");
      const bodySource = instructions || intro;
      return {
        headline,
        body: clampText(bodySource, 320),
      };
    }
    case "contract-type": {
      const headline = value || name || "New contract type";
      const bodySource = guidelines || (name && value ? `${name} (${value})` : name);
      return {
        headline,
        body: clampText(bodySource, 320),
      };
    }
    case "draft-template": {
      const headline = value || name || "New template";
      const bodySource = blurb || (name && value ? `${name} · ${value}` : name);
      return {
        headline,
        body: clampText(bodySource, 320),
      };
    }
    case "draft-clause-library": {
      const headline = sectionKey || clauseCategory || posture || "Clause variant";
      const bodySource = language || [clauseCategory, posture].filter(Boolean).join(" · ");
      return {
        headline,
        body: clampText(bodySource, 320),
      };
    }
    default:
      return { headline: "Proposal", body: "" };
  }
}

export type WriteApprovalPreviewField = {
  label: string;
  value: string;
  multiline?: boolean;
};

export type WriteApprovalCompactPreview = {
  title: string;
  shortCode?: string;
  summary: string;
};

export function getWriteApprovalCompactPreview(
  kind: WriteApprovalEntityKind,
  proposal: Record<string, unknown>
): WriteApprovalCompactPreview {
  const name = str(proposal.name);
  const value = str(proposal.value);
  const roleKey = str(proposal.role_key);
  const { headline, body } = formatWriteApprovalSummary(kind, proposal);

  switch (kind) {
    case "custom-role":
    case "draft-legal-role":
      return {
        title: name || headline,
        shortCode: roleKey || undefined,
        summary: clampText(body, 240),
      };
    case "contract-type":
      return {
        title: name || headline,
        shortCode: value || undefined,
        summary: clampText(body, 240),
      };
    case "draft-template":
      return {
        title: name || headline,
        shortCode: value || undefined,
        summary: clampText(body, 240),
      };
    case "draft-clause-library":
      return {
        title: headline,
        shortCode: str(proposal.section_key) || undefined,
        summary: clampText(body, 240),
      };
    default:
      return { title: headline, summary: body };
  }
}

export type WriteApprovalThreadReviewPreview = {
  title: string;
  shortCode?: string;
  purposeLine: string;
};

export function getWriteApprovalPlanGuidelineParagraph(
  kind: WriteApprovalEntityKind,
  mode: "create" | "update"
): string {
  const action = mode === "create" ? "creating" : "updating";
  switch (kind) {
    case "contract-type":
      return mode === "create"
        ? "Review the contract type details and guidelines below. Ask for changes in chat anytime, then use Create when you are ready."
        : "Review the proposed updates to this contract type below. Request changes in chat if anything should differ, then use Update to save.";
    case "custom-role":
      return `Review the role details and instructions below while ${action} this custom role. Use chat to refine the proposal, then ${mode === "create" ? "Create" : "Update"} when it looks right.`;
    case "draft-legal-role":
      return `Review the draft legal role details and instructions below. Request edits in chat as needed, then ${mode === "create" ? "Create" : "Update"} to save.`;
    case "draft-template":
      return `Review the template fields and structure below. Ask for changes in chat, then ${mode === "create" ? "Create" : "Update"} the template when you are satisfied.`;
    case "draft-clause-library":
      return `Review the clause variant details and language below. Refine via chat if needed, then ${mode === "create" ? "Create" : "Update"} to add it to the library.`;
    default:
      return "Review the proposal below, request changes in chat, then confirm when ready.";
  }
}

export type WriteApprovalPlanSection = {
  id: string;
  title: string;
  fields?: WriteApprovalPreviewField[];
  body?: string;
};

const CONSIDERATION_LABELS =
  /^(Purpose|Jurisdiction|Country|Category|Review focus|Compliance|Liability|Termination|Tax|GDPR|Scope):/im;

function extractConsiderations(text: string): { considerations: string; remainder: string } {
  const trimmed = text.trim();
  if (!trimmed || !CONSIDERATION_LABELS.test(trimmed)) {
    return { considerations: "", remainder: trimmed };
  }
  const lines = trimmed.split(/\n/);
  const considerationLines: string[] = [];
  const otherLines: string[] = [];
  for (const line of lines) {
    if (CONSIDERATION_LABELS.test(line.trim())) {
      considerationLines.push(line);
    } else if (considerationLines.length > 0 && line.trim() && !/^[A-Z][a-z]+(\s+\/[a-z]+)?:/.test(line.trim())) {
      considerationLines.push(line);
    } else {
      otherLines.push(line);
    }
  }
  if (considerationLines.length === 0) {
    return { considerations: "", remainder: trimmed };
  }
  return {
    considerations: considerationLines.join("\n").trim(),
    remainder: otherLines.join("\n").trim(),
  };
}

function structureToDisplay(structure: unknown): string {
  if (structure == null) return "";
  if (typeof structure === "string") return structure.trim();
  try {
    return JSON.stringify(structure, null, 2);
  } catch {
    return String(structure);
  }
}

export type WriteApprovalPlanFieldCard = {
  id: string;
  label: string;
  value: string;
  format?: "markdown" | "plain" | "code";
  span?: "full" | "half";
};

export type WriteApprovalPlanItemCard = {
  id: string;
  title: string;
  description: string;
  statusLabel?: string;
};

export type WriteApprovalPlanCardGroup =
  | {
      id: string;
      title: string;
      layout: "field-grid";
      fields: WriteApprovalPlanFieldCard[];
    }
  | {
      id: string;
      title: string;
      layout: "item-list";
      items: WriteApprovalPlanItemCard[];
    };

const LABELLED_LINE =
  /^(Purpose|Jurisdiction(?:\s*\/\s*country)?|Country|Category|Review focus|Compliance|Liability|Termination|Tax|GDPR|Scope(?:\s*&\s*purpose)?):\s*(.*)$/i;

const MARKDOWN_HEADING = /^#{1,6}\s+(.+)$/;

const BOLD_LABELLED_LINE =
  /^\*\*(Purpose|Jurisdiction(?:\s*\/\s*country)?|Country|Category|Review focus|Compliance|Liability|Termination|Tax|GDPR|Scope(?:\s*&\s*purpose)?)\*\*:?\s*(.*)$/i;

const BULLET_LABELLED_LINE =
  /^[-•]\s*(Purpose|Jurisdiction(?:\s*\/\s*country)?|Country|Category|Review focus|Compliance|Liability|Termination|Tax|GDPR|Scope(?:\s*&\s*purpose)?):\s*(.*)$/i;

function matchLabelledLine(trimmed: string): { rawLabel: string; rest: string } | null {
  let match = trimmed.match(LABELLED_LINE);
  if (match) {
    return { rawLabel: match[1] ?? "", rest: match[2]?.trim() ?? "" };
  }
  match = trimmed.match(MARKDOWN_HEADING);
  if (match) {
    const heading = (match[1] ?? "").replace(/\*\*/g, "").trim();
    return { rawLabel: heading, rest: "" };
  }
  match = trimmed.match(BOLD_LABELLED_LINE);
  if (match) {
    return { rawLabel: match[1] ?? "", rest: match[2]?.trim() ?? "" };
  }
  match = trimmed.match(BULLET_LABELLED_LINE);
  if (match) {
    return { rawLabel: match[1] ?? "", rest: match[2]?.trim() ?? "" };
  }
  return null;
}

function stripInlineMarkdown(text: string): string {
  return text
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/^[-•]\s+/gm, "");
}

export function parseLabelledBlocks(text: string): { label: string; body: string }[] {
  const trimmed = text.trim();
  if (!trimmed) return [];

  const blocks: { label: string; body: string }[] = [];
  let current: { label: string; lines: string[] } | null = null;

  for (const line of trimmed.split(/\n/)) {
    const labelled = matchLabelledLine(line.trim());
    if (labelled) {
      if (current) {
        blocks.push({
          label: current.label,
          body: current.lines.join("\n").trim(),
        });
      }
      const label = normalizeBlockLabel(labelled.rawLabel);
      current = { label, lines: [labelled.rest] };
    } else if (current && line.trim()) {
      current.lines.push(line.trim());
    } else if (!current && line.trim()) {
      return [{ label: "Guidelines", body: trimmed }];
    }
  }
  if (current) {
    blocks.push({
      label: current.label,
      body: current.lines.join("\n").trim(),
    });
  }
  return blocks.length > 0 ? blocks : [{ label: "Guidelines", body: trimmed }];
}

function normalizeBlockLabel(raw: string): string {
  const lower = raw.toLowerCase();
  if (lower.startsWith("jurisdiction") || lower === "country") return "Governing jurisdiction";
  if (lower.startsWith("purpose") || lower.startsWith("scope")) return "Scope & purpose";
  if (lower === "category") return "Category";
  if (lower.startsWith("review focus")) return "Review focus";
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

function slugId(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 48);
}

function blocksToItemCards(blocks: { label: string; body: string }[]): WriteApprovalPlanItemCard[] {
  const items: WriteApprovalPlanItemCard[] = [];
  for (const block of blocks) {
    if (/review focus/i.test(block.label) && /[-•]\s/.test(block.body)) {
      const bullets = block.body.split(/\n/).filter((l) => /^[-•]\s*/.test(l.trim()));
      if (bullets.length > 1) {
        for (const bullet of bullets) {
          const text = bullet.replace(/^[-•]\s*/, "").trim();
          const dash = text.indexOf(":");
          const title = dash > 0 ? text.slice(0, dash).trim() : text.slice(0, 60);
          const description = dash > 0 ? text.slice(dash + 1).trim() : text;
          items.push({
            id: slugId(title),
            title,
            description: description || text,
            statusLabel: "Proposed",
          });
        }
        continue;
      }
    }
    items.push({
      id: slugId(block.label),
      title: block.label,
      description: block.body,
      statusLabel: "Proposed",
    });
  }
  return items;
}

const PURPOSE_LINE_MAX = 180;

function firstSentenceClamp(text: string, maxLen: number): string {
  const normalized = stripInlineMarkdown(text).replace(/\s+/g, " ").trim();
  if (!normalized) return "";
  const sentenceMatch = normalized.match(/^[^.!?]+[.!?]?/);
  const sentence = (sentenceMatch?.[0] ?? normalized).trim();
  return clampText(sentence, maxLen);
}

function purposeLineFromLabelledText(text: string): string {
  const blocks = parseLabelledBlocks(text);
  const scope = blocks.find((b) => b.label === "Scope & purpose");
  if (scope?.body) return firstSentenceClamp(scope.body, PURPOSE_LINE_MAX);
  const purpose = blocks.find((b) => /purpose|scope/i.test(b.label));
  if (purpose?.body) return firstSentenceClamp(purpose.body, PURPOSE_LINE_MAX);
  if (blocks.length === 1 && blocks[0]?.label === "Guidelines" && blocks[0].body) {
    return firstSentenceClamp(blocks[0].body, PURPOSE_LINE_MAX);
  }
  return firstSentenceClamp(text, PURPOSE_LINE_MAX);
}

function extractProposalPurposeLine(
  kind: WriteApprovalEntityKind,
  proposal: Record<string, unknown>
): string {
  const guidelines = str(proposal.guidelines);
  const instructions = str(proposal.instructions);
  const blurb = str(proposal.blurb);
  const language = str(proposal.language);

  switch (kind) {
    case "contract-type":
      return guidelines
        ? purposeLineFromLabelledText(guidelines)
        : firstSentenceClamp(formatWriteApprovalSummary(kind, proposal).body, PURPOSE_LINE_MAX);
    case "custom-role":
    case "draft-legal-role":
      return instructions
        ? purposeLineFromLabelledText(instructions)
        : firstSentenceClamp(formatWriteApprovalSummary(kind, proposal).body, PURPOSE_LINE_MAX);
    case "draft-template":
      return blurb
        ? firstSentenceClamp(blurb, PURPOSE_LINE_MAX)
        : firstSentenceClamp(formatWriteApprovalSummary(kind, proposal).body, PURPOSE_LINE_MAX);
    case "draft-clause-library":
      return language
        ? firstSentenceClamp(language, PURPOSE_LINE_MAX)
        : firstSentenceClamp(formatWriteApprovalSummary(kind, proposal).body, PURPOSE_LINE_MAX);
    default:
      return firstSentenceClamp(formatWriteApprovalSummary(kind, proposal).body, PURPOSE_LINE_MAX);
  }
}

export function getWriteApprovalThreadReviewPreview(
  kind: WriteApprovalEntityKind,
  proposal: Record<string, unknown>
): WriteApprovalThreadReviewPreview {
  const compact = getWriteApprovalCompactPreview(kind, proposal);
  const purposeLine = extractProposalPurposeLine(kind, proposal);
  return {
    title: compact.title,
    shortCode: compact.shortCode,
    purposeLine: purposeLine || compact.summary.slice(0, PURPOSE_LINE_MAX),
  };
}

function uniqueSuggestions(items: string[], max = 4): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const item of items) {
    const t = item.trim();
    if (!t || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
    if (out.length >= max) break;
  }
  return out;
}

export function getWriteApprovalPlanHeroLabels(kind: WriteApprovalEntityKind): {
  primaryLabel: string;
  shortCodeLabel: string;
} {
  switch (kind) {
    case "contract-type":
      return { primaryLabel: "Name", shortCodeLabel: "Short name" };
    case "custom-role":
    case "draft-legal-role":
      return { primaryLabel: "Role name", shortCodeLabel: "Role key" };
    case "draft-template":
      return { primaryLabel: "Template name", shortCodeLabel: "Template code" };
    case "draft-clause-library":
      return { primaryLabel: "Variant", shortCodeLabel: "Section" };
    default:
      return { primaryLabel: "Name", shortCodeLabel: "Code" };
  }
}

export function getWriteApprovalRevisionSuggestions(
  kind: WriteApprovalEntityKind,
  proposal: Record<string, unknown>
): string[] {
  const guidelines = str(proposal.guidelines);
  const blocks = guidelines ? parseLabelledBlocks(guidelines) : [];
  const jurisdiction =
    blocks.find((b) => b.label === "Governing jurisdiction")?.body ??
    str(proposal.jurisdiction_applicability);
  const hasCategory = blocks.some((b) => b.label === "Category" && b.body.trim());

  switch (kind) {
    case "contract-type": {
      const dynamic: string[] = [];
      if (/luxembourg/i.test(jurisdiction)) {
        dynamic.push("Switch governing jurisdiction to Germany");
      } else if (/germany/i.test(jurisdiction)) {
        dynamic.push("Switch governing jurisdiction to Luxembourg");
      }
      if (!hasCategory) dynamic.push("Add a category to the guidelines");
      return uniqueSuggestions([
        ...dynamic,
        "Refine scope and purpose wording",
        "Change governing jurisdiction",
        "Update short code",
      ]);
    }
    case "custom-role":
    case "draft-legal-role":
      return uniqueSuggestions([
        "Expand role instructions",
        "Update role key",
        "Clarify scope in instructions",
        "Tighten compliance requirements",
      ]);
    case "draft-template":
      return uniqueSuggestions([
        "Refine template blurb",
        "Update template code",
        "Adjust document structure",
        "Bump version number",
      ]);
    case "draft-clause-library":
      return uniqueSuggestions([
        "Refine clause language",
        "Change posture (e.g. mutual / one-sided)",
        "Update jurisdiction applicability",
        "Adjust section or category",
      ]);
    default:
      return uniqueSuggestions(["Request a change to this proposal"]);
  }
}

function fieldCard(
  id: string,
  label: string,
  value: string,
  opts?: { span?: "full" | "half"; format?: WriteApprovalPlanFieldCard["format"] }
): WriteApprovalPlanFieldCard | null {
  const v = value.trim();
  if (!v) return null;
  return {
    id,
    label,
    value: v,
    span: opts?.span ?? "half",
    format: opts?.format ?? (v.includes("\n") || v.length > 120 ? "markdown" : "plain"),
  };
}

export function getWriteApprovalPlanCardGroups(
  kind: WriteApprovalEntityKind,
  _mode: "create" | "update",
  proposal: Record<string, unknown>
): WriteApprovalPlanCardGroup[] {
  const groups: WriteApprovalPlanCardGroup[] = [];

  switch (kind) {
    case "contract-type": {
      return groups;
    }
    case "custom-role":
    case "draft-legal-role": {
      const name = str(proposal.name);
      const roleKey = str(proposal.role_key);
      const instructions = str(proposal.instructions);
      const fields: WriteApprovalPlanFieldCard[] = [];
      const n = fieldCard("name", "Name", name);
      const rk = fieldCard("role-key", "Role key", roleKey);
      if (n) fields.push(n);
      if (rk) fields.push(rk);
      if (fields.length > 0) {
        groups.push({
          id: "details",
          title: "Role details",
          layout: "field-grid",
          fields,
        });
      }
      if (instructions) {
        const blocks = parseLabelledBlocks(instructions);
        const items = blocksToItemCards(blocks);
        groups.push({
          id: "instructions",
          title: "Role instructions",
          layout: "item-list",
          items:
            items.length > 0
              ? items
              : [
                  {
                    id: "instructions-body",
                    title: "Instructions",
                    description: instructions,
                    statusLabel: "Proposed",
                  },
                ],
        });
      }
      break;
    }
    case "draft-template": {
      const fields: WriteApprovalPlanFieldCard[] = [];
      const push = (c: WriteApprovalPlanFieldCard | null) => {
        if (c) fields.push(c);
      };
      push(fieldCard("name", "Name", str(proposal.name)));
      push(fieldCard("code", "Template code", str(proposal.value)));
      push(fieldCard("version", "Version", str(proposal.version)));
      push(fieldCard("blurb", "Blurb", str(proposal.blurb), { span: "full" }));
      const structure = structureToDisplay(proposal.structure);
      push(fieldCard("structure", "Structure", structure, { span: "full", format: "code" }));
      if (fields.length > 0) {
        groups.push({
          id: "details",
          title: "Template details",
          layout: "field-grid",
          fields,
        });
      }
      break;
    }
    case "draft-clause-library": {
      const fields: WriteApprovalPlanFieldCard[] = [];
      const push = (c: WriteApprovalPlanFieldCard | null) => {
        if (c) fields.push(c);
      };
      push(fieldCard("section", "Section", str(proposal.section_key)));
      push(fieldCard("category", "Category", str(proposal.clause_category)));
      push(fieldCard("posture", "Posture", str(proposal.posture)));
      push(fieldCard("jurisdiction", "Jurisdiction", str(proposal.jurisdiction_applicability)));
      push(fieldCard("version", "Version", str(proposal.version)));
      if (fields.length > 0) {
        groups.push({
          id: "details",
          title: "Clause variant details",
          layout: "field-grid",
          fields,
        });
      }
      const language = str(proposal.language);
      if (language) {
        groups.push({
          id: "language",
          title: "Clause language",
          layout: "item-list",
          items: [
            {
              id: "clause-text",
              title: "Language",
              description: language,
              statusLabel: "Proposed",
            },
          ],
        });
      }
      break;
    }
    default:
      break;
  }

  return groups;
}

export function getWriteApprovalPlanSections(
  kind: WriteApprovalEntityKind,
  mode: "create" | "update",
  proposal: Record<string, unknown>
): WriteApprovalPlanSection[] {
  const name = str(proposal.name);
  const { headline } = formatWriteApprovalSummary(kind, proposal);
  const displayName = name || headline;
  const action = mode === "create" ? "Create" : "Update";

  const sections: WriteApprovalPlanSection[] = [
    {
      id: "overview",
      title: "Plan Overview",
      body: `${action} ${entityKindLabel(kind)}: **${displayName}**. Review the proposed fields below, request changes in chat, or use Create when ready.`,
    },
  ];

  const previewFields = getWriteApprovalPreviewFields(kind, proposal);
  sections.push({
    id: "changes",
    title: "Proposed Changes",
    fields: previewFields,
  });

  const guidelines = str(proposal.guidelines);
  const instructions = str(proposal.instructions);
  const blurb = str(proposal.blurb);
  const language = str(proposal.language);

  if (kind === "draft-template" && blurb && !sections.some((s) => s.id === "blurb")) {
    sections.push({
      id: "blurb-detail",
      title: "Details",
      body: blurb,
    });
  }

  if (kind === "draft-clause-library" && language) {
    sections.push({
      id: "details",
      title: "Details",
      body: language,
    });
  }

  if (guidelines) {
    const { considerations, remainder } = extractConsiderations(guidelines);
    sections.push({
      id: "guidelines",
      title: "Guidelines",
      body: remainder || guidelines,
    });
    if (considerations) {
      sections.push({
        id: "considerations",
        title: "Validation / Considerations",
        body: considerations,
      });
    }
  } else if (instructions) {
    sections.push({
      id: "instructions",
      title: "Guidelines / Instructions",
      body: instructions,
    });
  } else if (blurb && kind === "draft-template") {
    sections.push({
      id: "blurb",
      title: "Details",
      body: blurb,
    });
  }

  return sections;
}

function entityKindLabel(kind: WriteApprovalEntityKind): string {
  switch (kind) {
    case "custom-role":
      return "custom role";
    case "contract-type":
      return "contract type";
    case "draft-template":
      return "draft template";
    case "draft-legal-role":
      return "draft legal role";
    case "draft-clause-library":
      return "clause variant";
    default:
      return "item";
  }
}

export function getWriteApprovalPreviewFields(
  kind: WriteApprovalEntityKind,
  proposal: Record<string, unknown>
): WriteApprovalPreviewField[] {
  const name = str(proposal.name);
  const value = str(proposal.value);
  const roleKey = str(proposal.role_key);
  const guidelines = str(proposal.guidelines);
  const instructions = str(proposal.instructions);
  const blurb = str(proposal.blurb);
  const sectionKey = str(proposal.section_key);
  const clauseCategory = str(proposal.clause_category);
  const posture = str(proposal.posture);
  const language = str(proposal.language);
  const jurisdiction = str(proposal.jurisdiction_applicability);
  const version = str(proposal.version);

  switch (kind) {
    case "custom-role":
    case "draft-legal-role":
      return [
        { label: "Name", value: name },
        { label: "Role key", value: roleKey },
        { label: "Role instructions", value: instructions, multiline: true },
      ];
    case "contract-type":
      return [
        { label: "Name", value: name },
        { label: "Short code", value: value },
        { label: "Guidelines", value: guidelines, multiline: true },
      ];
    case "draft-template":
      return [
        { label: "Name", value: name },
        { label: "Template code", value: value },
        { label: "Blurb", value: blurb, multiline: true },
        { label: "Version", value: version },
        {
          label: "Structure",
          value: structureToDisplay(proposal.structure),
          multiline: true,
        },
      ];
    case "draft-clause-library":
      return [
        { label: "Section", value: sectionKey },
        { label: "Category", value: clauseCategory },
        { label: "Posture", value: posture },
        { label: "Jurisdiction", value: jurisdiction },
        { label: "Version", value: version },
        { label: "Language", value: language, multiline: true },
      ];
    default:
      return [];
  }
}

const ENTITY_LIST_LABEL: Record<WriteApprovalEntityKind, string> = {
  "custom-role": "Custom Roles",
  "contract-type": "Contract Types",
  "draft-template": "Draft Templates",
  "draft-legal-role": "Draft Legal Roles",
  "draft-clause-library": "Clause Library",
};

export function writeApprovalSuccessMessageText(
  kind: WriteApprovalEntityKind,
  mode: "create" | "update",
  displayName: string
): string {
  const name = displayName.trim() || "Item";
  const listLabel = ENTITY_LIST_LABEL[kind];
  if (mode === "update") {
    switch (kind) {
      case "custom-role":
        return `Role updated successfully.\n\n**${name}** has been updated in Custom Roles.`;
      case "contract-type":
        return `Contract type updated successfully.\n\n**${name}** has been updated in Contract Types.`;
      case "draft-template":
        return `Template updated successfully.\n\n**${name}** has been updated in Draft Templates.`;
      case "draft-legal-role":
        return `Draft legal role updated successfully.\n\n**${name}** has been updated in Draft Legal Roles.`;
      case "draft-clause-library":
        return `Clause variant updated successfully.\n\n**${name}** has been updated in the Clause Library.`;
      default:
        return `Updated successfully.\n\n**${name}** has been updated in ${listLabel}.`;
    }
  }
  switch (kind) {
    case "custom-role":
      return `Role created successfully.\n\n**${name}** has been added to Custom Roles.`;
    case "contract-type":
      return `Contract type created successfully.\n\n**${name}** has been added to Contract Types.`;
    case "draft-template":
      return `Template created successfully.\n\n**${name}** has been added to Draft Templates.`;
    case "draft-legal-role":
      return `Draft legal role created successfully.\n\n**${name}** has been added to Draft Legal Roles.`;
    case "draft-clause-library":
      return `Clause variant created successfully.\n\n**${name}** has been added to the Clause Library.`;
    default:
      return `Saved successfully.\n\n**${name}** has been added to ${listLabel}.`;
  }
}
